import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import type { CreateScheduleInput, UpdateScheduleInput } from '../types';

function useScheduleApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).schedules, [spaceId]);
}

export function useSchedules(params?: { status?: string; agentId?: string }) {
  const spaceId = useSpaceId();
  const api = useScheduleApi();
  return useQuery({
    queryKey: ['space', spaceId, 'schedules', params],
    queryFn: () => api.list(params),
  });
}

export function useAgentSchedules(agentId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useScheduleApi();
  return useQuery({
    queryKey: ['space', spaceId, 'schedules', { agentId }],
    queryFn: () => api.list({ agentId: agentId! }),
    enabled: !!agentId,
  });
}

export function useSchedule(scheduleId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useScheduleApi();
  return useQuery({
    queryKey: ['space', spaceId, 'schedules', scheduleId],
    queryFn: () => api.get(scheduleId!),
    enabled: !!scheduleId,
  });
}

export function useCreateSchedule() {
  const spaceId = useSpaceId();
  const api = useScheduleApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateScheduleInput) => api.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'schedules'] });
    },
  });
}

export function useUpdateSchedule() {
  const spaceId = useSpaceId();
  const api = useScheduleApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scheduleId, input }: { scheduleId: string; input: UpdateScheduleInput }) =>
      api.update(scheduleId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'schedules'] });
    },
  });
}

export function useDeleteSchedule() {
  const spaceId = useSpaceId();
  const api = useScheduleApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (scheduleId: string) => api.delete(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'schedules'] });
    },
  });
}
